import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { BarChart2, ArrowUpDown, Lightbulb, Maximize2, X } from 'lucide-react';
import { mockLCAData } from '../data/mockLCAData';

const MaterialComparison = ({ selectedImpact, lcaData }) => {
  const [comparisonMode, setComparisonMode] = useState('materials'); // Only materials mode now
  const [hoveredBar, setHoveredBar] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [sortOrder, setSortOrder] = useState('highToLow'); // 'highToLow', 'lowToHigh', 'byCategory'
  const [searchTerm, setSearchTerm] = useState(''); // For filtering components
  
  useEffect(() => {
    if (!showPopup) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [showPopup]);


  
  // Use actual data if available, fallback to mock data
  const dataSource = lcaData || mockLCAData;
  const selectedImpactInfo = dataSource.impactCategories.find(cat => cat.id === selectedImpact);
  
  const getComparisonData = () => {
    // Compare top 6 building components by impact value
    return dataSource.components
      .sort((a, b) => (b.impacts[selectedImpact] || 0) - (a.impacts[selectedImpact] || 0))
      .slice(0, 6)
      .map(component => ({
        name: component.name,
        value: component.impacts[selectedImpact] || 0,
        category: component.category,
        color: getCategoryColor(component.category)
      }));
  };
  
  const getCategoryColor = (category) => {
    const colors = {
      'Structure': '#0D9488',      // Teal
      'Envelope': '#F59E0B',       // Yellow
      'MEP': '#1E3A8A',            // Navy Blue
      'Finishes': '#EA580C',       // Orange
      'Foundation': '#1E40AF',     // Dark Blue
      'Roofing': '#0891B2',        // Teal Blue
      'Electrical': '#FCD34D',     // Light Yellow
      'HVAC': '#FB923C',           // Light Orange
      'Plumbing': '#0EA5E9',       // Sky Blue
      'Interior': '#16A34A',       // Green
      'Exterior': '#F97316',       // Orange
      'Landscaping': '#22C55E',    // Green
      'Other': '#6B7280',          // Gray
      'Others': '#6B7280'          // Gray (plural)
    };
    return colors[category] || '#6b7280';
  };
  
  // Only render if we have data
  if (!dataSource || !dataSource.components || dataSource.components.length === 0) {
    return (
      <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center text-gray-800">
            <BarChart2 className="w-5 h-5 mr-2 text-emerald-600" />
            Impact by Components
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-gray-500 py-8">
            <BarChart2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>Upload an Excel file to see component impact analysis</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show loading state if data is being processed
  if (!lcaData) {
    return (
      <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center text-gray-800">
            <BarChart2 className="w-5 h-5 mr-2 text-emerald-600" />
            Impact by Components
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-gray-500 py-8">
            <BarChart2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>Loading data...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const comparisonData = getComparisonData();
  const maxValue = Math.max(...comparisonData.map(item => item.value));

  // Summary metrics across ALL components (not just top 6)
  const totalComponentsCount = dataSource.components.length;
  const totalImpactAllComponents = dataSource.components.reduce(
    (sum, component) => sum + (component.impacts[selectedImpact] || 0),
    0
  );
  
  // Debug logging for impact calculation
  console.log('Selected impact:', selectedImpact);
  console.log('Components:', dataSource.components);
  console.log('Total impact calculated:', totalImpactAllComponents);
  const categoryTotals = dataSource.components.reduce((acc, component) => {
    const categoryName = component.category || 'Others';
    const value = component.impacts[selectedImpact] || 0;
    acc[categoryName] = (acc[categoryName] || 0) + value;
    return acc;
  }, {});
  const [highestCategoryName, highestCategoryValue] = Object.entries(categoryTotals)
    .reduce((maxEntry, entry) => (entry[1] > (maxEntry?.[1] || 0) ? entry : maxEntry), ['—', 0]);
  
  // Calculate total building impact per m²/year
  let totalBuildingImpactPerM2PerYear = 0;
  
  // Try different possible data structures
  const buildingArea = dataSource.building?.area || dataSource.buildingInfo?.area;
  const buildingServiceLife = dataSource.building?.serviceLife || dataSource.buildingInfo?.serviceLife;
  
  if (buildingArea && buildingServiceLife && buildingArea > 0 && buildingServiceLife > 0) {
    totalBuildingImpactPerM2PerYear = totalImpactAllComponents / buildingArea / buildingServiceLife;
  }
  
  // Debug logging
  console.log('Building data:', dataSource.building);
  console.log('Building info:', dataSource.buildingInfo);
  console.log('Total impact:', totalImpactAllComponents);
  console.log('Area found:', buildingArea);
  console.log('Service life found:', buildingServiceLife);
  console.log('Calculated per m²/year:', totalBuildingImpactPerM2PerYear);
  const sortedAllComponentsDesc = [...dataSource.components]
    .sort((a, b) => (b.impacts[selectedImpact] || 0) - (a.impacts[selectedImpact] || 0));
  const topN = Math.max(1, Math.ceil(0.2 * totalComponentsCount));
  const topImpactSum = sortedAllComponentsDesc
    .slice(0, topN)
    .reduce((sum, component) => sum + (component.impacts[selectedImpact] || 0), 0);
  const top20PercentShare = totalImpactAllComponents > 0
    ? Math.round((topImpactSum / totalImpactAllComponents) * 100)
    : 0;
  
  // Debug logging for Top 20% calculation
  console.log('Top 20% calculation debug:');
  console.log('Total components:', totalComponentsCount);
  console.log('Top N components (20%):', topN);
  console.log('Top components:', sortedAllComponentsDesc.slice(0, topN).map(c => ({ name: c.name, impact: c.impacts[selectedImpact] })));
  console.log('Top impact sum:', topImpactSum);
  console.log('Total impact all components:', totalImpactAllComponents);
  console.log('Top 20% share calculated:', top20PercentShare + '%');

  // Helper function to get all components sorted by selected criteria and filtered by search
  const getSortedAllComponents = () => {
    let allComponents = dataSource.components.map(component => ({
      name: component.name,
      value: component.impacts[selectedImpact] || 0,
      category: component.category,
      color: getCategoryColor(component.category)
    }));

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      allComponents = allComponents.filter(component => 
        component.name.toLowerCase().includes(searchLower) ||
        component.category.toLowerCase().includes(searchLower)
      );
    }

    if (sortOrder === 'highToLow') {
      return allComponents.sort((a, b) => b.value - a.value);
    } else if (sortOrder === 'lowToHigh') {
      return allComponents.sort((a, b) => a.value - b.value);
    } else if (sortOrder === 'byCategory') {
      // Sort by category in the same order as pie chart, then by value within each category
      const categoryOrder = ['Structure', 'Envelope', 'Finishes', 'MEP', 'Others'];
      return allComponents.sort((a, b) => {
        const aIndex = categoryOrder.indexOf(a.category);
        const bIndex = categoryOrder.indexOf(b.category);
        
        if (aIndex !== bIndex) {
          return aIndex - bIndex; // Sort by category order
        } else {
          return b.value - a.value; // High to low within same category
        }
      });
    }
    return allComponents;
  };

  // Helper function to get category breakdown
  const getCategoryBreakdown = () => {
    const breakdown = {};
    dataSource.components.forEach(component => {
      const category = component.category || 'Others';
      breakdown[category] = (breakdown[category] || 0) + 1;
    });
    
    // Log all found categories for debugging
    console.log('Found categories:', Object.keys(breakdown));
    
    // Return breakdown in the correct order, but include all found categories
    const orderedBreakdown = {};
    const categoryOrder = ['Structure', 'Envelope', 'Finishes', 'MEP', 'Others'];
    
    // First add categories in the preferred order
    categoryOrder.forEach(category => {
      if (breakdown[category]) {
        orderedBreakdown[category] = breakdown[category];
      }
    });
    
    // Then add any remaining categories that weren't in the preferred order
    Object.keys(breakdown).forEach(category => {
      if (!categoryOrder.includes(category)) {
        orderedBreakdown[category] = breakdown[category];
      }
    });
    
    return orderedBreakdown;
  };

  return (
    <>
    <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-md">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center text-gray-800">
            <BarChart2 className="w-5 h-5 mr-2 text-emerald-600" />
            Impact by Components
          </CardTitle>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowPopup(true)}
              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
            >
              <Maximize2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pb-8">
        <div className="space-y-6">
          {/* Chart Title */}
          <div className="text-center">
            <h3 className="text-sm font-semibold text-gray-800 md:text-base">
              Building Components - {selectedImpactInfo?.shortName} Impact
            </h3>
            <p className="text-sm text-gray-600 mt-3">
              Compare environmental impact across different building components
            </p>
          </div>
          
          {/* Bar Chart */}
          <div className="space-y-3">
            {comparisonData.map((item, index) => (
              <div 
                key={item.name}
                className="group cursor-pointer"
                onMouseEnter={() => setHoveredBar(item)}
                onMouseLeave={() => setHoveredBar(null)}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center space-x-3">
                    <div 
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: item.color }}
                    />
                    <div>
                      <span className="font-medium text-gray-800 group-hover:text-emerald-600 transition-colors text-sm">
                        {item.name}
                      </span>
                      {item.category && (
                        <Badge variant="secondary" className="ml-2 text-xs">
                          {item.category}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-gray-900 text-sm">
                      {item.value.toLocaleString()}
                      <span className="text-xs text-gray-500 ml-1.5">{selectedImpactInfo?.unit}</span>
                      <span className="text-xs text-gray-500 ml-1.5">
                        ({((item.value / totalImpactAllComponents) * 100).toFixed(1)}%)
                      </span>
                    </div>

                  </div>
                </div>
                
                {/* Horizontal Bar */}
                <div className="relative">
                  <div className="w-full bg-gray-200 rounded-md h-[18px] overflow-hidden mt-2">
                    <div
                      className={`h-full rounded-md transition-all duration-200 ease-out ${
                        hoveredBar?.name === item.name ? 'scale-105 shadow-lg' : ''
                      }`}
                      style={{
                        width: `${(item.value / maxValue) * 100}%`,
                        backgroundColor: item.color,
                        animationDelay: `${index * 150}ms`
                      }}
                    />
                  </div>
                  
                  {/* Value Label */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-[10px] font-semibold text-white drop-shadow-lg">
                      {item.value.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          

          

        </div>
        

             </CardContent>
     </Card>

     {/* Popup Modal - All Components with Sorting */}
     {showPopup && (
       <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm p-2 md:items-center">
         <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] h-full max-h-[95vh] overflow-y-auto md:overflow-hidden">
           <div className="flex items-center justify-center p-6 border-b border-gray-200">
             <h2 className="text-xl font-semibold text-gray-900 flex items-center">
               <BarChart2 className="w-5 h-5 mr-3 text-emerald-600" />
               Impact by Components - All Components
             </h2>
             <Button
               variant="ghost"
               size="sm"
               onClick={() => setShowPopup(false)}
               className="text-gray-500 hover:text-gray-700 ml-4"
             >
               <X className="w-5 h-5" />
             </Button>
           </div>
           
          <div className="flex flex-col lg:flex-row h-full">
            {/* Left side - All Components Chart */}
            <div className="flex-1 order-2 lg:order-1 flex flex-col items-center p-4 sm:p-6 overflow-hidden border-t border-gray-200 lg:border-t-0">
               {/* Search and Sort Options */}
               <div className="w-full mb-6">
                 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                   {/* Search Bar */}
                   <div className="w-full sm:flex-1 relative sm:max-w-md">
                     <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                       <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                       </svg>
                     </div>
                     <input
                       type="text"
                       placeholder="Search components..."
                       value={searchTerm}
                       onChange={(e) => setSearchTerm(e.target.value)}
                       className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-colors"
                     />
                     {searchTerm && (
                       <button
                         onClick={() => setSearchTerm('')}
                         className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                       >
                         <X className="w-4 h-4" />
                       </button>
                     )}
                   </div>
                   
                   {/* Sort Options */}
                   <div className="grid grid-cols-2 sm:grid-cols-3 sm:flex sm:items-center sm:space-x-2 sm:gap-2 gap-2">
                     <Button
                       variant={sortOrder === 'highToLow' ? 'default' : 'outline'}
                       size="sm"
                       onClick={() => setSortOrder('highToLow')}
                       className={sortOrder === 'highToLow' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                     >
                       High→Low
                     </Button>
                     <Button
                       variant={sortOrder === 'lowToHigh' ? 'default' : 'outline'}
                       size="sm"
                       onClick={() => setSortOrder('lowToHigh')}
                       className={sortOrder === 'lowToHigh' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                     >
                       Low→High
                     </Button>
                     <Button
                       variant={sortOrder === 'byCategory' ? 'default' : 'outline'}
                       size="sm"
                       onClick={() => setSortOrder('byCategory')}
                       className={sortOrder === 'byCategory' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                     >
                       By Category
                     </Button>
                   </div>
                 </div>
               </div>

               {/* Scrollable Chart Container */}
               <div className="flex-1 w-full pr-2 overflow-y-auto">
                 <div className="space-y-3">
                   {getSortedAllComponents().length === 0 ? (
                     <div className="text-center py-8 text-gray-500">
                       <p>No components found matching "{searchTerm}"</p>
                       <p className="text-sm mt-1">Try adjusting your search terms</p>
                     </div>
                   ) : (
                     getSortedAllComponents().map((item, index) => (
                     <div 
                       key={item.name}
                       className="group cursor-pointer"
                       onMouseEnter={() => setHoveredBar(item)}
                       onMouseLeave={() => setHoveredBar(null)}
                     >
                       <div className="flex items-center justify-between mb-1.5">
                         <div className="flex items-center space-x-3">
                           <div 
                             className="w-4 h-4 rounded"
                             style={{ backgroundColor: item.color }}
                           />
                <div>
                             <span className="font-medium text-gray-800 group-hover:text-emerald-600 transition-colors text-sm">
                               {item.name}
                             </span>
                             <Badge variant="secondary" className="ml-2 text-xs">
                               {item.category}
                             </Badge>
                           </div>
                         </div>
                         <div className="text-right">
                           <div className="font-bold text-gray-900 text-sm">
                             {item.value.toLocaleString()}
                             <span className="text-xs text-gray-500 ml-1">{selectedImpactInfo?.unit}</span>
                             <span className="text-xs text-gray-500 ml-1">
                               ({((item.value / totalImpactAllComponents) * 100).toFixed(1)}%)
                             </span>
                  </div>
                </div>
              </div>
                       
                       {/* Horizontal Bar */}
                       <div className="relative">
                         <div className="w-full bg-gray-200 rounded-md h-5 overflow-hidden">
                           <div
                             className={`h-full rounded-md transition-all duration-200 ease-out ${
                               hoveredBar?.name === item.name ? 'scale-105 shadow-lg' : ''
                             }`}
                             style={{
                               width: `${(item.value / maxValue) * 100}%`,
                               backgroundColor: item.color,
                               animationDelay: `${index * 150}ms`
                             }}
                           />
                         </div>
                         
                         {/* Value Label */}
                         <div className="absolute inset-0 flex items-center justify-center">
                           <span className="text-xs font-semibold text-white drop-shadow-lg">
                             {item.value.toLocaleString()}
                           </span>
                </div>
              </div>
            </div>
                   ))
          )}
                 </div>
               </div>
             </div>
          
            {/* Right side - Summary and Insights */}
            <div className="flex-1 order-1 lg:order-2 p-4 sm:p-6 border-b border-gray-200 lg:border-b-0 lg:border-l flex flex-col overflow-hidden">
              <div className="flex-1 pr-0 sm:pr-2 space-y-6 overflow-y-auto">
          {/* Summary Statistics */}
                 <div>
                   <h3 className="text-lg font-semibold text-gray-800 mb-3">Summary Statistics</h3>
                   <div className="grid grid-cols-3 sm:grid-cols-1 gap-2 sm:gap-4">
                     <div className="flex flex-col items-center justify-center gap-1 rounded-lg bg-gray-50 p-2 sm:p-3">
                       <div className="text-base sm:text-xl font-bold text-gray-800 leading-tight">
                         {totalComponentsCount}
                       </div>
                       <div className="text-[10px] sm:text-xs text-gray-600 whitespace-nowrap">Total Components</div>
                     </div>
                     <div className="flex flex-col items-center justify-center gap-1 rounded-lg bg-emerald-50 p-2 text-center sm:p-3">
                       <div className="text-base font-bold text-emerald-600 leading-tight sm:text-xl">
                         {totalBuildingImpactPerM2PerYear.toFixed(2)}
                       </div>
                       <div className="text-[9px] text-gray-500 sm:text-xs">
                         kg CO₂-eq/m²/year
                       </div>
                       <div className="text-[10px] text-gray-600 sm:text-xs">Total Building Impact</div>
                     </div>
                     <div className="flex flex-col items-center justify-center gap-1 rounded-lg bg-blue-50 p-2 sm:p-3">
                       <div className="text-base sm:text-xl font-bold text-blue-600 leading-tight">
                         {top20PercentShare}%
                       </div>
                       <div className="text-[10px] sm:text-xs text-gray-600 text-center leading-tight">Top 20% of Components Contribution</div>
                     </div>
                   </div>
              </div>

                 {/* Category Breakdown */}
                 <div className="flex-1 flex flex-col min-h-0">
                   <h3 className="text-lg font-semibold text-gray-800 mb-3">Category Breakdown</h3>
                   <div className="flex-1 pr-0 sm:pr-2 overflow-y-auto">
                     <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-1 gap-2">
                       {Object.entries(getCategoryBreakdown()).map(([category, count]) => (
                         <div
                           key={category}
                           className="flex flex-col items-center gap-1 rounded-lg bg-gray-50 p-2 text-center sm:flex-row sm:items-center sm:justify-between sm:gap-2 sm:p-3 sm:text-left"
                         >
                           <div className="flex w-full items-center justify-center gap-2 overflow-hidden sm:justify-start">
                             <div
                               className="h-3 w-3 flex-shrink-0 rounded-full"
                               style={{ backgroundColor: getCategoryColor(category) }}
                             />
                             <span className="truncate text-xs font-medium text-gray-800 sm:text-sm">
                               {category}
                             </span>
                           </div>
                           <span className="text-[11px] text-gray-600 sm:flex-shrink-0 sm:text-xs sm:whitespace-nowrap">
                             {count} components
                           </span>
                         </div>
                       ))}
                     </div>
              </div>
            </div>
          </div>
        </div>
                </div>
            </div>
          </div>
        )}
    </>
  );
};

export default MaterialComparison;